import React from 'react';
import PropTypes from 'prop-types';
import strings from 'localization/strings';
import { useAppContext } from 'context/AppContext';
import { makeStyles } from 'styles/util';
import { mustBeNumber } from 'form/validation';
import { createEndAdornment } from 'helpers/FormHelper';
import { getShortDateFormat } from 'helpers/DateHelper';
import { useField } from 'react-final-form';
import { startOfDay } from 'date-fns';
import { validateTimePeriodPriceFormData, getTimePeriodPriceDifferences } from 'logic/timePeriodPriceLogic';
import clsx from 'clsx';

import Box from '@material-ui/core/Box';
import { TextField, Checkboxes, showErrorOnBlur } from 'mui-rff';
import NullableDatePicker from 'form/NullableDatePicker';
import IconButton from '@material-ui/core/IconButton';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import TooltipIcon from 'common/TooltipIcon';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const useStyles = makeStyles(({ theme, colors }) => ({
    timePeriodPriceContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        '& .Mui-disabled p': {
            color: colors.textColorDisabled
        }
    },
    amountContainer: {
        flex: '1 1 20%',
    },
    vatRateContainer: {
        flex: '1 1 20%',
    },
    eligibleForVatContainer: {
        flex: '1 1 20%',
        marginTop: theme.spacing(1.375)
    },
    commissionRateContainer: {
        flex: '1 1 20%',
    },
    startDateContainer: {
        flex: '1 1 20%',
    },
    endDateContainer: {
        flex: '1 1 20%',
    },
    removeButtonContainer: {
        flex: `0 0 ${theme.spacing(9)}px`,
        alignSelf: 'center',
        justifyContent: 'flex-end',
        display: 'flex',
        alignItems: 'center'
    },
    errorIcon: {
        color: colors.danger,
        margin: 0
    },
    warningIcon: {
        color: colors.warning,
        margin: 0
    },
    informationIcon: {
        margin: 0
    },
    errorBackground: {
        backgroundColor: colors.errorBackground
    }
}));

const TimePeriodPriceEditor = ({ fieldNamePrefix, amountLabel, currency, referenceDictionary, vatRateVisible, commissionRateVisible, eligibleForVatVisible, timePeriodVisible, linkIconVisible, commissionRateEnabled = true, canRemove, onRemove }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const getName = suffix => suffix ? `${fieldNamePrefix}.${suffix}` : fieldNamePrefix;
    const value = useField(getName()).input.value;
    const today = startOfDay(new Date());
    let minimumEndDate = value.startDate;
    if(value.inUse && (!minimumEndDate || minimumEndDate < today)) {
        minimumEndDate = today;
    }

    const errorMessage = validateTimePeriodPriceFormData(value, appContext);

    let linkIcon;
    if(linkIconVisible) {
        if(value.referenceKey) {
            const referenceTpp = referenceDictionary
                ? referenceDictionary[value.referenceKey]
                : undefined;
            const differences = getTimePeriodPriceDifferences(value, referenceTpp, currency, appContext);
            const differenceString = differences
                .map(o => `${o.label}: ${o.formatter(o.formValue)} / ${o.formatter(o.referenceValue)}`)
                .join(', ') + '.';
            const text = strings.timePeriodPriceHasReference +
                (differences.length === 0 ? '' : ' ' + strings.timePeriodPriceHasReferenceWithDifference + ' ' + differenceString);
            linkIcon = <TooltipIcon icon={<LinkIcon/>} text={text} className={differences.length === 0 ? classes.informationIcon : classes.warningIcon} />;
        } else {
            linkIcon = <TooltipIcon icon={<LinkOffIcon/>} text={strings.timePeriodPriceHasNoReference} className={classes.informationIcon} />;
        }
    }

    return (
        <Box className={clsx(classes.timePeriodPriceContainer, {[classes.errorBackground]: !!errorMessage})}>
            <Box className={classes.amountContainer}>
                <TextField
                    name={getName('amount')}
                    size="small"
                    label={amountLabel}
                    helperText={strings.enterAmountExcludingVat}
                    variant="outlined"
                    showError={showErrorOnBlur}
                    disabled={!value.editable}
                    required
                    fieldProps={{ validate: mustBeNumber }}
                    InputProps={createEndAdornment(currency)}
                />
            </Box>

            {
                vatRateVisible &&
                (
                    <Box className={classes.vatRateContainer}>
                        <TextField
                            name={getName('vatRate')}
                            size="small"
                            label={strings.paymentDocuments.vatRate}
                            variant="outlined"
                            showError={showErrorOnBlur}
                            disabled={!value.editable}
                            required
                            fieldProps={{ validate: mustBeNumber }}
                            InputProps={createEndAdornment('%')}
                        />
                    </Box>
                )
            }

            {
                eligibleForVatVisible &&
                (
                    <Box className={classes.eligibleForVatContainer}>
                        <Checkboxes
                            name={getName('eligibleForVat')}
                            data={
                                { label: strings.eligibleForVat, value: true }
                            }
                            disabled={!value.editable}
                        />
                    </Box>
                )
            }

            {
                commissionRateVisible &&
                (
                    <Box className={classes.commissionRateContainer}>
                        <TextField
                            name={getName('commissionRate')}
                            size="small"
                            label={strings.commissionRate}
                            variant="outlined"
                            showError={showErrorOnBlur}
                            disabled={!value.editable || !commissionRateEnabled}
                            required
                            fieldProps={{ validate: mustBeNumber }}
                            InputProps={createEndAdornment('%')}
                        />
                    </Box>
                )
            }

            <Box className={classes.startDateContainer}>
                {
                    timePeriodVisible &&
                    (
                        <NullableDatePicker
                            name={getName('startDate')}
                            size="small"
                            label={strings.startDate}
                            nullString={strings.fromBeginning}
                            nullButtonLabel={strings.fromBeginning}
                            formatFunc={getShortDateFormat}
                            inputVariant="outlined"
                            showError={showErrorOnBlur}
                            disabled={!value.editable}
                        />
                    )
                }
            </Box>

            <Box className={classes.endDateContainer}>
                {
                    timePeriodVisible &&
                    (
                        <NullableDatePicker
                            name={getName('endDate')}
                            size="small"
                            label={strings.endDate}
                            nullString={strings.toEnd}
                            nullButtonLabel={strings.toEnd}
                            formatFunc={getShortDateFormat}
                            inputVariant="outlined"
                            showError={showErrorOnBlur}
                            disabled={!value.endDateEditable}
                            fieldProps={{ minDate: minimumEndDate }}
                        />
                    )
                }
            </Box>

            <Box className={classes.removeButtonContainer}>
                {
                    errorMessage &&
                    (
                        <TooltipIcon icon={<ErrorOutlineIcon/>} text={errorMessage} className={classes.errorIcon} />
                    )
                }
                {
                    !errorMessage && !value.editable &&
                    (
                        <TooltipIcon text={strings.priceCantBeEditedDueToBeingInUse} className={classes.informationIcon} />
                    )
                }
                {
                    linkIcon
                }
                <IconButton
                    disabled={!canRemove || !value.editable}
                    onClick={onRemove}
                >
                    <DeleteOutlineOutlinedIcon/>
                </IconButton>
            </Box>
        </Box>
    );
};

TimePeriodPriceEditor.propTypes = {
    fieldNamePrefix: PropTypes.string.isRequired,
    amountLabel: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    referenceDictionary: PropTypes.object,
    vatRateVisible: PropTypes.bool,
    eligibleForVatVisible: PropTypes.bool,
    commissionRateVisible: PropTypes.bool,
    timePeriodVisible: PropTypes.bool,
    linkIconVisible: PropTypes.bool,
    commissionRateEnabled: PropTypes.bool,
    canRemove: PropTypes.bool,
    onRemove: PropTypes.func.isRequired
};

export default TimePeriodPriceEditor;
